import { useState, useEffect } from 'react';
import { useCountry } from '../contexts/CountryContext';
import { api } from '../lib/api';
import { formatNumber, getStatusColor, COUNTRY_FLAGS } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Store,
  Building2,
  Calendar,
  AlertTriangle,
  FileWarning,
  Clock,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

export default function DashboardPage() {
  const { selectedCountry, getCountryName } = useCountry();
  const [stats, setStats] = useState(null);
  const [countryStats, setCountryStats] = useState([]);
  const [recentActivity, setRecentActivity] = useState({ collections: [], exceptions: [] });
  const [vendorHealth, setVendorHealth] = useState({ top_vendors: [], low_performers: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [statsData, countryData, activityData, healthData] = await Promise.all([
          api.getDashboardStats(selectedCountry),
          api.getCountryStats(),
          api.getRecentActivity(selectedCountry, 10),
          api.getVendorHealth(selectedCountry, 5),
        ]);
        setStats(statsData);
        setCountryStats(countryData);
        setRecentActivity(activityData);
        setVendorHealth(healthData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedCountry]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Operations Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          {selectedCountry
            ? `Showing data for ${getCountryName(selectedCountry)}`
            : 'Overview across all European markets'}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Active Stores"
          value={stats?.active_stores || 0}
          icon={Store}
          trend={null}
          color="text-primary"
        />
        <KPICard
          title="Active Vendors"
          value={stats?.active_vendors || 0}
          icon={Building2}
          trend={null}
          color="text-accent"
        />
        <KPICard
          title="Collections This Week"
          value={stats?.collections_this_week || 0}
          icon={Calendar}
          trend={null}
          color="text-blue-600"
        />
        <KPICard
          title="Open Exceptions"
          value={stats?.open_exceptions || 0}
          icon={AlertTriangle}
          trend={null}
          color={stats?.critical_exceptions > 0 ? 'text-destructive' : 'text-warning'}
          badge={stats?.critical_exceptions > 0 ? `${stats.critical_exceptions} critical` : null}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Missed This Month</p>
                <p className="text-2xl font-bold text-foreground">{stats?.missed_this_month || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expiring Docs (30d)</p>
                <p className="text-2xl font-bold text-foreground">{stats?.expiring_docs_30d || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <FileWarning className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Reviews</p>
                <p className="text-2xl font-bold text-foreground">{stats?.pending_reviews || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Country Overview */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span>Country Overview</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {countryStats.map((country) => (
                <div
                  key={country.code}
                  className="p-4 border border-border rounded-lg hover:border-primary/30 transition-colors cursor-pointer"
                  data-testid={`country-card-${country.code}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xl">{COUNTRY_FLAGS[country.code] || '🌍'}</span>
                    <Badge
                      variant={country.health === 'green' ? 'default' : country.health === 'amber' ? 'secondary' : 'destructive'}
                      className={
                        country.health === 'green'
                          ? 'bg-green-100 text-green-800'
                          : country.health === 'amber'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }
                    >
                      {country.health}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-foreground">{country.name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Stores</span>
                      <span className="font-medium text-foreground">{country.stores}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Vendors</span>
                      <span className="font-medium text-foreground">{country.vendors}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Exceptions</span>
                      <span className={`font-medium ${country.exceptions > 0 ? 'text-destructive' : 'text-foreground'}`}>
                        {country.exceptions}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Vendor Health */}
        <Card>
          <CardHeader>
            <CardTitle>Vendor Health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {vendorHealth.top_vendors?.length > 0 ? (
              <>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    Top Performers
                  </h4>
                  <div className="space-y-2">
                    {vendorHealth.top_vendors.slice(0, 3).map((vendor) => (
                      <div key={vendor.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <span className="text-sm font-medium truncate">{vendor.name}</span>
                        <Badge className="bg-green-100 text-green-800">
                          {vendor.performance_score}%
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
                
                {vendorHealth.low_performers?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                      Needs Attention
                    </h4>
                    <div className="space-y-2">
                      {vendorHealth.low_performers.slice(0, 3).map((vendor) => (
                        <div key={vendor.id} className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-100">
                          <span className="text-sm font-medium truncate">{vendor.name}</span>
                          <Badge className="bg-red-100 text-red-800">
                            {vendor.performance_score}%
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No vendor data available
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Collections */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Recent Collections
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.collections?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.collections.slice(0, 5).map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-3 border border-border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {event.status === 'completed' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : event.status === 'missed' ? (
                        <XCircle className="w-5 h-5 text-red-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-blue-600" />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {event.waste_stream?.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(event.scheduled_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge className={getStatusColor(event.status)}>
                      {event.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No recent collections
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Exceptions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Recent Exceptions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.exceptions?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.exceptions.slice(0, 5).map((exception) => (
                  <div
                    key={exception.id}
                    className="flex items-center justify-between p-3 border border-border rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium">{exception.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {exception.exception_type} • {new Date(exception.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={getStatusColor(exception.severity)}>
                      {exception.severity}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No recent exceptions
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KPICard({ title, value, icon: Icon, trend, color, badge }) {
  return (
    <Card className="card-hover" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold text-foreground mt-1">{formatNumber(value)}</p>
            {badge && (
              <Badge variant="destructive" className="mt-2">
                {badge}
              </Badge>
            )}
          </div>
          <div className={`p-3 bg-muted rounded-lg ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-5 w-96 mt-2" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardContent className="pt-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
